import { macroRecordingCatalogRequestSchema, hostRecordingCatalogPageSchema,
  type MacroRecordingCatalogReply, type MacroRecordingCatalogRequest } from '@playcastdotio/Messaging';
import type { RawPackageUploadClient } from './MacroPackageCaptureRelay';

/** Current peer ownership gates disk discovery. Replies after owner loss can never expose a replacement session's catalog. */
export class RemoteRecordingCatalogManager {
  private readonly pending = new Map<string, { peerId: string; retired: boolean }>();
  public constructor(private readonly reader: RawPackageUploadClient, private readonly hostId: () => string) {}

  public async handle(peerId: string, payload: unknown): Promise<MacroRecordingCatalogReply | null> {
    const parsed = macroRecordingCatalogRequestSchema.safeParse(payload);
    if (!parsed.success) return null;
    const request = parsed.data;
    if (request.hostId !== this.hostId()) return this.failed(request, 'recording_catalog_host_changed');
    if (!this.reader.catalog) return this.failed(request, 'recording_catalog_unavailable');
    if (this.pending.has(request.requestId) || this.pending.size >= 8) return this.failed(request, 'recording_catalog_busy');
    const owner = { peerId, retired: false };
    this.pending.set(request.requestId, owner);
    try {
      const page = hostRecordingCatalogPageSchema.parse(await this.reader.catalog(request.campaignId, request.cursor));
      if (owner.retired || request.hostId !== this.hostId()) return this.failed(request, 'recording_catalog_owner_lost');
      return { requestId: request.requestId, hostId: request.hostId, campaignId: request.campaignId,
        sessionId: request.sessionId, success: true, page };
    } catch { return this.failed(request, owner.retired ? 'recording_catalog_owner_lost' : 'recording_catalog_verification_failed'); }
    finally { this.pending.delete(request.requestId); }
  }

  public release(peerId: string): void { for (const owner of this.pending.values()) if (owner.peerId === peerId) owner.retired = true; }
  private failed(request: MacroRecordingCatalogRequest, error: string): MacroRecordingCatalogReply {
    return { requestId: request.requestId, hostId: request.hostId, campaignId: request.campaignId, sessionId: request.sessionId, success: false, error };
  }
}
